import { Upload, X } from "lucide-react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";

import { AdminSections } from "@/api/admin/teamRoles";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePermission } from "@/services/casl/usePermission";
import { cn } from "@/utils/cn/cn";

interface UploadTabProps {
  file?: File;
  error?: string;
  disabled?: boolean;
  onChange: (file?: File) => void;
}

export const UploadTab = ({
  file,
  error,
  disabled,
  onChange,
}: UploadTabProps) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const selectedFile = acceptedFiles[0];
      if (selectedFile) {
        onChange(selectedFile);
      }
    },
    [onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || !!file,
    multiple: false,
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
  });

  const hasUpdatePermission = usePermission(AdminSections.BRAND, {
    isUpdate: true,
  });

  const uploadContent = (
    <div className="flex flex-col gap-4">
      {file ? (
        <div className="flex items-center justify-between p-3 border rounded-md bg-white border-gray-300">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="bg-[#fdfaee] p-2 rounded-md">
              <Upload className="size-5 text-gray-700" />
            </div>
            <span className="font-medium truncate">{file.name}</span>
          </div>
          <button
            onClick={() => onChange(undefined)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
            type="button"
          >
            <X className="size-5 text-gray-500" />
          </button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={cn(
            "flex flex-col items-center justify-center border border-dashed rounded-2xl bg-[#f7f7f7] min-h-[220px] transition-all duration-200 cursor-pointer",
            isDragActive
              ? "border-primary bg-primary/5"
              : "border-gray-300 hover:border-gray-400",
            (disabled || !hasUpdatePermission) &&
              "opacity-50 cursor-not-allowed"
          )}
        >
          <input {...getInputProps()} disabled={!hasUpdatePermission} />
          <div className="bg-[#fdfaee] rounded-md p-3 mb-5">
            <Upload className="size-6" />
          </div>
          <p className="text-base font-bold">
            {isDragActive ? "Drop your file here" : "Drop your menu PDF here"}
          </p>
          <p className="text-gray-500 mt-2">
            or click to browse · file size up to 20 MB
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {disabled ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-not-allowed">{uploadContent}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                You already have a link set. Please remove it first to upload a
                file.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        uploadContent
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
